'use client';

import { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

interface PremiumStatus {
  isPremium: boolean;
  hasCompleteTier: boolean;
  tiers: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string | null;
  email: string | null;
}

/**
 * Hook to check user's premium status
 * Checks both:
 * 1. Supabase database (for logged-in users)
 * 2. localStorage (for anonymous purchases / dev mode)
 */
export function usePremiumStatus(): PremiumStatus {
  const [status, setStatus] = useState<PremiumStatus>({
    isPremium: false,
    hasCompleteTier: false,
    tiers: [],
    isLoading: true,
    isAuthenticated: false,
    userId: null,
    email: null,
  });

  useEffect(() => {
    async function checkStatus() {
      // Check localStorage first (for dev mode or anonymous purchases)
      const devMode = typeof window !== 'undefined' && localStorage.getItem('pathfinder_dev_mode') === 'true';
      const localPurchase = typeof window !== 'undefined' ? localStorage.getItem('pathfinder_purchase') : null;
      const localPurchaseData = localPurchase ? JSON.parse(localPurchase) : null;

      // If dev mode is on, grant premium access
      if (devMode) {
        setStatus({
          isPremium: true,
          hasCompleteTier: true,
          tiers: ['full_package'],
          isLoading: false,
          isAuthenticated: false,
          userId: null,
          email: null,
        });
        return;
      }

      // Check if user is authenticated
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // User is authenticated, check database for purchases
        try {
          const response = await fetch('/api/user/purchases');
          if (response.ok) {
            const data = await response.json();
            setStatus({
              isPremium: data.isPremium || !!localPurchaseData,
              hasCompleteTier: data.hasCompleteTier || ['complete', 'full_package'].includes(localPurchaseData?.tier),
              tiers: data.tiers || [],
              isLoading: false,
              isAuthenticated: true,
              userId: user.id,
              email: user.email || null,
            });
            return;
          }
        } catch (error) {
          console.error('Error fetching premium status:', error);
        }

        // Fallback to localStorage if API fails
        setStatus({
          isPremium: !!localPurchaseData,
          hasCompleteTier: ['complete', 'full_package'].includes(localPurchaseData?.tier),
          tiers: localPurchaseData ? [localPurchaseData.tier] : [],
          isLoading: false,
          isAuthenticated: true,
          userId: user.id,
          email: user.email || null,
        });
      } else {
        // Not authenticated, only use localStorage
        setStatus({
          isPremium: !!localPurchaseData,
          hasCompleteTier: ['complete', 'full_package'].includes(localPurchaseData?.tier),
          tiers: localPurchaseData ? [localPurchaseData.tier] : [],
          isLoading: false,
          isAuthenticated: false,
          userId: null,
          email: null,
        });
      }
    }

    checkStatus();

    // Listen for auth state changes
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkStatus();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return status;
}

/**
 * Simple helper to check localStorage purchase (for components that don't need full hook)
 */
export function getLocalPurchase(): { tier: string; purchasedAt: string } | null {
  if (typeof window === 'undefined') return null;

  const purchase = localStorage.getItem('pathfinder_purchase');
  if (!purchase) return null;

  try {
    return JSON.parse(purchase);
  } catch {
    return null;
  }
}

/**
 * Check if dev mode is enabled
 */
export function isDevMode(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('pathfinder_dev_mode') === 'true';
}
